import {
  sendParent, Machine, AnyEventObject, StateMachine,
} from 'xstate';
import { eq } from 'lodash/fp';
import * as Sentry from '@sentry/browser';
import { nanoid } from 'nanoid';
import * as Logrocket from 'logrocket';

type ErrorEvent = {
  id: string;
  type: 'ERROR';
  error: Error;
  subject?: string;
  data?: any;
};

interface ErrorContext {
  errors: ErrorEvent[];
}

interface ErrorSchema {
  states: {
    idle: {};
  };
}

export const createError = (
  error: Error,
  subject?: string,
  data?: any,
): ErrorEvent => ({
  id: nanoid(),
  type: 'ERROR',
  subject,
  data,
  error,
});

type GenericConsole = Pick<Console, 'error' | 'warn' | 'info' | 'debug'>;
const logger = (client: GenericConsole) => (_, evt: ErrorEvent) => {
  const { error, id, ...rest } = evt;
  client.error(evt.id, error);
  client.info({
    errorId: id,
    ...rest,
  });
};

const logToSentry = (_, evt: ErrorEvent) => {
  Sentry.captureException(evt.error, (scope) => {
    scope.setContext('Additional data', {
      errorId: evt.id,
    });

    return scope;
  });
};

const logToLogrocket = logger(Logrocket);
const logToConsole = logger(console);

const createErrorActions = (env: string) => {
  const coreErrorActions = [
    logToLogrocket,
    // logToSentry,
  ];

  if (['production', 'test'].some(eq(env))) {
    return coreErrorActions;
  }

  return [...coreErrorActions, logToConsole];
};

export const createErrorMachine = (
  id: string,
): StateMachine<unknown, ErrorSchema, ErrorEvent> => Machine<ErrorContext, ErrorSchema, ErrorEvent>({
  id,
  initial: 'idle',
  states: {
    idle: {
      on: {
        ERROR: {
          actions: createErrorActions(process.env.NODE_ENV as string),
        },
      },
    },
  },
});
